import { ReactNode, useMemo } from 'react';
import { TimeFilterType } from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { useTimeBasedChartPresentation } from './use-time-based-chart-presentation';

// ── Generic data types (decoupled from any specific API schema) ──

export type BarChartDataItem = {
  name?: string;
  aggValue?: string | number;
  values?: Array<{ key?: string; value?: string | number }>;
};

export type BarChartSummaryItem = {
  name?: string;
  total?: string | number;
  average?: string | number;
  count?: string | number;
};

export type BarChartDataResponse = {
  data?: BarChartDataItem[];
  summary?: BarChartSummaryItem[];
};

// ── Series config types ──

export type SingleSeriesConfig = {
  mode: 'single';
  seriesKey: string;
};

export type StackedSeriesConfig = {
  mode: 'stacked';
  seriesKeys: string[];
};

export type SeriesConfig = SingleSeriesConfig | StackedSeriesConfig;

// ── Chart data group shape (matches what Chart.BarChart expects) ──

export type ChartDataGroup = {
  name: string;
  rawName: string;
  values: Record<string, number>;
};

// ── Hook options & result ──

type UseTimeBasedBarChartOptions = {
  /** Response data from any analytics API. Structurally compatible with AirDataResponse. */
  data: BarChartDataResponse | undefined;
  /** Current x-axis time filter selection. */
  selectedXAxis: TimeFilterType;
  /** Defines how series are extracted from the data. */
  seriesConfig: SeriesConfig;
  /** When true, show per-group total in tooltip header. */
  showTotal?: boolean;
};

export type UseTimeBasedBarChartResult = {
  /** Transformed data ready for Chart.BarChart. */
  chartData: ChartDataGroup[];
  /** Tooltip title nodes keyed by group name. */
  tooltipTitlesByName: Record<string, ReactNode>;
  /** Tooltip header elements keyed by group name. */
  tooltipHeadersByName: Record<string, JSX.Element>;
  /** Aggregated totals per series across all groups. */
  seriesTotals: Record<string, number>;
};

/** Internal type alias for useTimeBasedChartPresentation's data parameter. */
type PresentationData = Parameters<typeof useTimeBasedChartPresentation>[0]['data'];

/**
 * Encapsulates data transformation and presentation logic for time-based bar charts.
 *
 * Handles both single-series and stacked (multi-series) charts.
 * Delegates to `useTimeBasedChartPresentation` for tooltip and series total computation.
 *
 * Data is passed in externally — this hook does NOT fetch data.
 *
 * @example
 * ```tsx
 * const { chartData, tooltipHeadersByName, seriesTotals } = useTimeBasedBarChart({
 *   data: apiResponse,
 *   selectedXAxis: TimeFilterType.DAILY,
 *   seriesConfig: { mode: 'single', seriesKey: 'Answered' },
 * });
 * ```
 */
export const useTimeBasedBarChart = ({
  data,
  selectedXAxis,
  seriesConfig,
  showTotal = false,
}: UseTimeBasedBarChartOptions): UseTimeBasedBarChartResult => {
  const chartData = useMemo<ChartDataGroup[]>(() => {
    if (!data?.data?.length) return [];

    if (seriesConfig.mode === 'single') {
      const { seriesKey } = seriesConfig;
      return data.data.map((group) => {
        const rawName = group.name ?? '';
        return {
          name: rawName,
          rawName,
          values: {
            [seriesKey]: Number(group.aggValue ?? 0),
          },
        };
      });
    }

    // Stacked / multi-series mode
    const { seriesKeys } = seriesConfig;
    return data.data.map((group) => {
      const rawName = group.name ?? '';
      const valuesByKey =
        group.values?.reduce<Record<string, number>>((acc, item) => {
          if (!item.key) return acc;
          acc[item.key] = Number(item.value ?? 0);
          return acc;
        }, {}) ?? {};

      return {
        name: rawName,
        rawName,
        values: seriesKeys.reduce<Record<string, number>>((acc, key) => {
          if (valuesByKey[key] && valuesByKey[key] > 0) {
            acc[key] = valuesByKey[key] ?? 0;
          }
          return acc;
        }, {}),
      };
    });
  }, [data, seriesConfig]);

  const { tooltipTitlesByName, tooltipHeadersByName, seriesTotals } = useTimeBasedChartPresentation({
    // BarChartDataResponse is structurally compatible with AirDataResponse.
    // The cast is safe and hidden from consumers of this hook.
    data: data as PresentationData,
    selectedXAxis,
    chartData,
    showTotal,
  });

  return { chartData, tooltipTitlesByName, tooltipHeadersByName, seriesTotals };
};
